import {
    NewRoutingRule,
    NewRoutingRuleAssignment,
    NewRoutingRuleResult,
    RoutingRuleCompanyRelationsParameters,
    RoutingRulePrepayment,
    RoutingRuleRateAssignment,
} from '@mews-commander/data-access';
import { uniqById } from '@mews/fp';
import { CompaniesFormFields, ItemsAndConditionsSchemaFormValues, RatesOptionsNames } from '../../../../../schemas';
import { getSelectedCompanyIds } from '../../../../../utils/companies';

interface NewRoutingRuleAssignmentWithRates extends NewRoutingRuleAssignment {
    rateAssignments?: RoutingRuleRateAssignment[];
}

interface NewRoutingRuleWithAssignments extends NewRoutingRule {
    assignments: NewRoutingRuleAssignmentWithRates[];
}

const filterMatchingEntities = ({
    selectedEntityIds,
    existingEntityIds,
}: {
    selectedEntityIds: string[] | null;
    existingEntityIds: string[] | null;
}) => {
    const selectedEntitiesCount = selectedEntityIds !== null ? selectedEntityIds.length : null;
    const existingEntitiesCount = existingEntityIds !== null ? existingEntityIds.length : null;

    if (selectedEntitiesCount === null && existingEntitiesCount === null) return true;
    if (selectedEntitiesCount === null || existingEntitiesCount === null) return false;
    return selectedEntityIds?.some((id) => existingEntityIds?.includes(id));
};

const filterRulesByCompanyRelations = ({
    rules,
    relationType,
    selectedCompanyIds,
}: {
    rules: NewRoutingRuleWithAssignments[];
    relationType: keyof RoutingRuleCompanyRelationsParameters;
    selectedCompanyIds: string[] | null;
}) =>
    rules.filter((rule) => {
        const existingCompanyIds =
            rule.companiesWithRelations.length === 0
                ? null
                : rule.companiesWithRelations
                      .filter((company) => company.companyRelations[relationType])
                      .map((company) => company.companyId);
        return filterMatchingEntities({
            selectedEntityIds: selectedCompanyIds,
            existingEntityIds: existingCompanyIds,
        });
    });

export const getConflicts = (
    selectedValues: ItemsAndConditionsSchemaFormValues,
    existingRulesData?: NewRoutingRuleResult,
    routingRuleId?: string
) => {
    if (existingRulesData == null) return null;

    const { routingRules, routingRuleAssignments, rateAssignments } = existingRulesData;
    const existingRules = routingRules?.filter((rule) => rule.id !== routingRuleId);
    const {
        services: selectedServices,
        companies: selectedCompanies,
        prepaidReservationsEnabled,
        ratesEnabled,
        companiesEnabled,
    } = selectedValues;

    const selectedPrepayment = prepaidReservationsEnabled ? RoutingRulePrepayment.Prepaid : RoutingRulePrepayment.All;

    const selectedServiceIds = Object.keys(selectedServices).filter((key) => selectedServices[key].serviceEnabled);

    const matchingServiceAssignments =
        routingRuleAssignments?.filter((assignment) => selectedServiceIds.includes(assignment.serviceId)) ?? [];

    if (matchingServiceAssignments.length === 0) return null;

    const matchingServiceAssignmentsWithRates = matchingServiceAssignments.map((assignment) => ({
        ...assignment,
        rateAssignments: rateAssignments?.filter(
            (rateAssignment) => rateAssignment.routingRuleAssignmentId === assignment.id
        ),
    }));

    const matchingRateAssignments = matchingServiceAssignmentsWithRates.filter((assignment) => {
        const existingRateIds =
            assignment?.rateAssignments?.length === 0
                ? null
                : assignment.rateAssignments?.map((rateAssignment) => rateAssignment.rateId);
        const selectedRateIds =
            !ratesEnabled || selectedServices[assignment.serviceId].serviceRates?.type === RatesOptionsNames.All
                ? null
                : selectedServices[assignment.serviceId].serviceRates?.selectedRates ?? [];
        return filterMatchingEntities({
            selectedEntityIds: selectedRateIds,
            existingEntityIds: existingRateIds ?? null,
        });
    });

    if (matchingRateAssignments.length === 0) return null;

    const matchingAssignmentRules =
        existingRules
            ?.filter((rule) => matchingRateAssignments?.some((assignment) => assignment.routingRuleId === rule.id))
            .map((rule) => ({
                ...rule,
                assignments: matchingRateAssignments.filter((assignment) => assignment.routingRuleId === rule.id),
            })) ?? [];

    const matchingPrepaymentRules = matchingAssignmentRules.filter((rule) => rule.prepayment === selectedPrepayment);

    if (matchingPrepaymentRules.length === 0) return null;

    const selectedCompanyIds = getSelectedCompanyIds({
        companiesEnabled,
        selectedCompanies,
        types: [CompaniesFormFields.LinkAsCompany],
    });

    const matchingCompaniesRules = filterRulesByCompanyRelations({
        rules: matchingPrepaymentRules,
        relationType: 'partnerCompany',
        selectedCompanyIds,
    });

    const selectedTravelAgencyIds = getSelectedCompanyIds({
        companiesEnabled,
        selectedCompanies,
        types: [CompaniesFormFields.LinkAsTravelAgency],
    });

    const matchingTravelAgenciesRules = filterRulesByCompanyRelations({
        rules: matchingPrepaymentRules,
        relationType: 'travelAgency',
        selectedCompanyIds: selectedTravelAgencyIds,
    });

    if (matchingTravelAgenciesRules.length === 0 && matchingCompaniesRules.length === 0) return null;

    const combinedRules = uniqById([...matchingCompaniesRules, ...matchingTravelAgenciesRules]);

    const conflictingExistingRules = combinedRules.map((rule) => ({
        id: rule.id,
        name: rule.name,
        services: rule.assignments.map((assignment) => ({
            id: assignment.serviceId,
            name: assignment.serviceName,
        })),
        rates: rule.assignments.map((assignment) => ({
            service: { id: assignment.serviceId, name: assignment.serviceName },
            rates: assignment.rateAssignments
                ?.filter(
                    (rateAssignment) =>
                        selectedServices[assignment.serviceId].serviceRates?.selectedRates?.includes(
                            rateAssignment.rateId
                        )
                )
                .map((rateAssignment) => ({
                    id: rateAssignment.rateId,
                    name: rateAssignment.rateName,
                })),
        })),
        companies: rule.companiesWithRelations
            .filter(
                (company) => company.companyRelations.partnerCompany && selectedCompanyIds?.includes(company.companyId)
            )
            .map((company) => ({ id: company.companyId, name: company.companyName })),
        travelAgencies: rule.companiesWithRelations
            .filter(
                (company) =>
                    company.companyRelations.travelAgency && selectedTravelAgencyIds?.includes(company.companyId)
            )
            .map((company) => ({ id: company.companyId, name: company.companyName })),
        prepayment: rule.prepayment === RoutingRulePrepayment.Prepaid,
    }));

    const ratesGroupedByService = conflictingExistingRules
        .flatMap((rule) => rule.rates)
        .reduce((acc: (typeof rate)[], rate) => {
            const index = acc.findIndex((service) => service.service.id === rate.service.id);
            if (index === -1) {
                acc.push(rate);
            }
            return acc;
        }, []);

    const allConflictingValues = {
        services: uniqById(conflictingExistingRules.flatMap((rule) => rule.services)),
        rates: ratesGroupedByService,
        companies: uniqById(conflictingExistingRules.flatMap((rule) => rule.companies)),
        travelAgencies: uniqById(conflictingExistingRules.flatMap((rule) => rule.travelAgencies)),
        prepayment: conflictingExistingRules.some((rule) => rule.prepayment),
    };

    return {
        allConflictingValues,
        conflictingExistingRules,
    };
};
